import React from 'react';
import cx from 'classnames';
import { MenuItem } from 'primereact/menuitem'; // eslint-disable-line import/no-unresolved
import { NavLink, useLocation } from 'react-router-dom';
import { MenuButton } from 'shared/components/common/buttons/MenuButton';
import classes from './NavMenu.module.scss';

type Props = {
  /** Additional className */
  className?: string;
  /** An array of menu items */
  model: NavMenuItem[];
};

type NavMenuItem = {
  className?: string;
  items?: NavMenuItem[];
  label?: string;
  path?: string;
  isExternalLink?: boolean;
};

export const NavMenu: React.VFC<Props> = ({ model, className }) => {
  const internalLinks = model.filter((menuItem) => !menuItem.isExternalLink);
  const externalLinks = model.filter((menuItem) => menuItem.isExternalLink);

  return (
    <div className={cx(classes.root, className)}>
      <div className={classes.navLinkContainer}>
        {internalLinks.map((menuItem) => {
          if (menuItem.items) {
            return <NavbarMenuButton key={menuItem.label} {...menuItem} />;
          }
          return <NavbarLink key={menuItem.label} {...menuItem} />;
        })}
      </div>
      {externalLinks.length > 0 && (
        <div className={cx(classes.navLinkContainer, classes.externalLinks)}>
          {externalLinks.map((menuItem) => {
            return (
              <a
                key={menuItem.label}
                href={menuItem.path}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.navLink}
              >
                {menuItem.label}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NavbarMenuButton: React.VFC<NavMenuItem> = ({ label, items = [] }) => {
  const location = useLocation();
  const makeItemTemplate =
    (item: NavMenuItem): MenuItem['template'] =>
    (_, options) =>
      (
        <div onClick={options.onClick}>
          <NavbarLink {...item} />
        </div>
      );

  return (
    <MenuButton
      className={cx(classes.toolsButton, {
        [classes.toolsButtonActive]: items.some(({ path }) => path && location.pathname.startsWith(path)),
      })}
      menuClassName={classes.toolsMenu}
      model={items.map((item) => ({ template: makeItemTemplate(item) }))}
      label={label}
      data-test={`menu-item-${label}`}
    />
  );
};

const NavbarLink: React.VFC<NavMenuItem> = ({ path = '#', className, label }) => {
  const location = useLocation();

  return (
    <NavLink
      to={path}
      className={({ isActive }) =>
        cx(classes.navLink, className, {
          [classes.navLinkActive]: isActive || location.pathname.includes(path),
        })
      }
      data-test={`menu-item-${label}`}
    >
      {label}
    </NavLink>
  );
};

export type { Props as NavMenuProps, NavMenuItem };
